import type { ReactNode } from 'react';
import { Fragment } from 'react';

import { Typography } from '@/modules/perseo-billing/ui/react/src/components/beta';

type LineState =
  | 'unselected'
  | 'selected'
  | 'empty'
  | 'activeStep'
  | 'disabledStep';

type TOrientation = 'horizontal' | 'vertical';

interface StepperProps {
  orientation: TOrientation;
  label: string;
  lines: LineState[];
  className?: string;
}

const Line = ({
  orientation,
  color
}: {
  orientation: TOrientation;
  color: string;
}) => (
  <span
    className={`co-flex ${
      orientation === 'vertical'
        ? 'co-h-full co-min-h-40px after:co-h-full after:co-w-[2px] after:co-border-r'
        : 'co-w-full co-min-w-40px after:co-w-full after:co-h-[2px] after:co-border-b'
    } co-items-center co-text-${color} after:co-content-[''] after:co-border-[#DBDBDB] after:co-border-2 after:co-inline-block`}
  ></span>
);

const EmptyLine = ({ orientation }: { orientation: TOrientation }) => (
  <span
    className={`co-flex co-items-center ${
      orientation === 'vertical'
        ? 'co-h-full co-min-h-40px'
        : 'co-w-full co-min-w-40px'
    }`}
  ></span>
);

const Step = ({ isActive }: { isActive: boolean }) => (
  <span
    className={`co-flex co-items-center co-justify-center co-min-w-[24px] co-min-h-[24px] co-bg-white co-border-2 ${
      isActive ? 'co-border-[#2E57F3]' : 'co-border-[#E6E6E6]'
    } co-rounded-full`}
  >
    {isActive && (
      <span className="co-w-16px co-h-16px co-bg-[#2E57F3] co-rounded-full"></span>
    )}
  </span>
);

const Stepper = ({ orientation, label, lines, className }: StepperProps) => {
  const lineComponents: { [key: string]: ReactNode } = {
    empty: <EmptyLine orientation={orientation} />,
    unselected: <Line orientation={orientation} color="#E6E6E6" />,
    selected: <Line orientation={orientation} color="#2E57F3" />,
    activeStep: <Step isActive />,
    disabledStep: <Step isActive={false} />
  };
  const hasActiveStep = lines.includes('activeStep');

  return (
    <div
      className={`co-flex ${
        orientation === 'vertical' ? 'co-flex-row' : 'co-flex-col'
      } ${className} co-gap-8px`}
    >
      <div
        className={`co-flex co-items-center ${
          orientation === 'vertical' ? 'co-flex-col' : 'co-flex-row'
        }`}
      >
        {lines.map((line, index) => (
          <Fragment key={index}>{lineComponents[line]}</Fragment>
        ))}
      </div>
      <Typography
        weight="semibold"
        color={hasActiveStep ? '#1245D6' : 'pds-neutral-600'}
        className={`co-self-center ${
          hasActiveStep ? 'co-text-[#1245D6]' : 'co-text-pds-neutral-600'
        }`}
        noWrap
      >
        {label}
      </Typography>
    </div>
  );
};

export default Stepper;
